
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts"
import { Loader2 } from "lucide-react"
import { useMetrics, useInvestors, useActivities } from "@/lib/use-api"
import { Skeleton } from "@/components/ui/skeleton"

const FUNNEL_COLORS = ["#ed4c4c", "#faa09a", "#ffd0cd", "#10b981", "#059669"]
const STAGE_COLORS: Record<string, string> = {
  Lead: "#3b82f6",
  "Meeting Scheduled": "#f59e0b",
  "Due Diligence": "#8b5cf6",
  "Term Sheet": "#ec4899",
  Closed: "#10b981",
  Active: "#10b981",
  Portfolio: "#8b5cf6",
  Passed: "#9ca3af",
}

const responseTimeData = [
  { week: "W1", hours: 6.2 },
  { week: "W2", hours: 5.1 },
  { week: "W3", hours: 4.8 },
  { week: "W4", hours: 3.9 },
  { week: "W5", hours: 4.2 },
  { week: "W6", hours: 3.5 },
]

export default function AnalyticsPage() {
  const { data: metrics, isLoading: metricsLoading } = useMetrics()
  const { data: investors, isLoading: investorsLoading } = useInvestors()
  const { data: activities, isLoading: activitiesLoading } = useActivities()

  const isLoading = metricsLoading || investorsLoading || activitiesLoading

  // Build funnel data from metrics
  const funnelData = metrics
    ? Object.entries(metrics.investors_by_stage)
        .map(([stage, count]) => ({ stage, count }))
        .sort((a, b) => b.count - a.count)
    : []

  // Build stage distribution from metrics
  const stageDistribution = metrics
    ? Object.entries(metrics.investors_by_stage).map(([name, value]) => ({
        name,
        value,
        color: STAGE_COLORS[name] || "#9ca3af",
      }))
    : []

  // Build engagement data from activities (group by month)
  const engagementData = (() => {
    if (!activities || activities.length === 0) {
      return [
        { month: "Jan", emails: 0, views: 0, meetings: 0 },
        { month: "Feb", emails: 0, views: 0, meetings: 0 },
      ]
    }
    const months: Record<string, { emails: number; views: number; meetings: number }> = {}
    for (const act of activities) {
      const date = new Date(act.created_at)
      const month = date.toLocaleString("default", { month: "short" })
      if (!months[month]) months[month] = { emails: 0, views: 0, meetings: 0 }
      if (act.type === "email") months[month].emails++
      else if (act.type === "view") months[month].views++
      else if (act.type === "meeting") months[month].meetings++
    }
    return Object.entries(months).map(([month, data]) => ({ month, ...data }))
  })()

  // Summary stats from real data
  const totalInvestors = metrics?.total_investors ?? 0
  const avgEngagement = metrics ? Math.round(metrics.avg_engagement_score) : 0
  const recentActivities = metrics?.recent_activities ?? 0

  if (isLoading) {
    return (
      <div className="p-6 lg:p-8">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-2 h-5 w-72" />
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-80 rounded-xl" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Analytics</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Track investor engagement, conversion rates, and response metrics.
        </p>
      </div>

      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          {
            label: "Total Investors",
            value: String(totalInvestors),
            sub: `${recentActivities} recent activities`,
          },
          {
            label: "Pipeline Stages",
            value: String(funnelData.length),
            sub: "active stages",
          },
          {
            label: "Avg Engagement",
            value: String(avgEngagement),
            sub: "out of 100",
          },
          {
            label: "Deal Rooms",
            value: String(metrics?.active_deal_rooms ?? 0),
            sub: "currently active",
          },
        ].map((stat) => (
          <div
            key={stat.label}
            className="glass-strong rounded-xl p-4 shadow-sm"
          >
            <p className="text-xs font-medium text-muted-foreground">
              {stat.label}
            </p>
            <p className="mt-1 text-2xl font-bold text-foreground">
              {stat.value}
            </p>
            <p className="text-[10px] text-muted-foreground">{stat.sub}</p>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Conversion Funnel */}
        <div className="glass-strong rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-foreground">
            Investor Pipeline
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Investors by stage
          </p>
          <div className="mt-4 h-64">
            {funnelData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={funnelData}
                  layout="vertical"
                  margin={{ top: 0, right: 10, left: 30, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(5, 30%, 92%)"
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: "hsl(0, 0%, 40%)" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="stage"
                    tick={{ fontSize: 11, fill: "hsl(0, 0%, 40%)" }}
                    axisLine={false}
                    tickLine={false}
                    width={110}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "rgba(255,255,255,0.9)",
                      backdropFilter: "blur(8px)",
                      border: "1px solid hsl(5, 30%, 90%)",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                  />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                    {funnelData.map((entry, index) => (
                      <Cell
                        key={`cell-${entry.stage}`}
                        fill={
                          FUNNEL_COLORS[index % FUNNEL_COLORS.length]
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center">
                <p className="text-sm text-muted-foreground">
                  No investor data yet
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Stage Distribution */}
        <div className="glass-strong rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-foreground">
            Investor Stage Distribution
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Current pipeline breakdown
          </p>
          <div className="mt-4 h-64 flex items-center justify-center">
            {stageDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stageDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {stageDistribution.map((entry) => (
                      <Cell
                        key={`cell-${entry.name}`}
                        fill={entry.color}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "rgba(255,255,255,0.9)",
                      backdropFilter: "blur(8px)",
                      border: "1px solid hsl(5, 30%, 90%)",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground">
                No investor data yet
              </p>
            )}
          </div>
          {stageDistribution.length > 0 && (
            <div className="flex flex-wrap justify-center gap-4 mt-2">
              {stageDistribution.map((item) => (
                <div key={item.name} className="flex items-center gap-1.5">
                  <div
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-xs text-muted-foreground">
                    {item.name} ({item.value})
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Engagement Over Time */}
        <div className="glass-strong rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-foreground">
            Activity Over Time
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Emails, views, and meetings by month
          </p>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={engagementData}
                margin={{ top: 5, right: 5, left: -20, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(5, 30%, 92%)"
                />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fill: "hsl(0, 0%, 40%)" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "hsl(0, 0%, 40%)" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid hsl(5, 30%, 90%)",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                />
                <Bar
                  dataKey="emails"
                  fill="#ed4c4c"
                  radius={[4, 4, 0, 0]}
                  name="Emails"
                />
                <Bar
                  dataKey="views"
                  fill="#faa09a"
                  radius={[4, 4, 0, 0]}
                  name="Views"
                />
                <Bar
                  dataKey="meetings"
                  fill="#10b981"
                  radius={[4, 4, 0, 0]}
                  name="Meetings"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Response Time */}
        <div className="glass-strong rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-foreground">
            Response Time Trend
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Average hours to respond to investor queries
          </p>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={responseTimeData}
                margin={{ top: 5, right: 5, left: -20, bottom: 0 }}
              >
                <defs>
                  <linearGradient
                    id="responseGrad"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor="#ed4c4c"
                      stopOpacity={0.2}
                    />
                    <stop
                      offset="95%"
                      stopColor="#ed4c4c"
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(5, 30%, 92%)"
                />
                <XAxis
                  dataKey="week"
                  tick={{ fontSize: 11, fill: "hsl(0, 0%, 40%)" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "hsl(0, 0%, 40%)" }}
                  axisLine={false}
                  tickLine={false}
                  unit="h"
                />
                <Tooltip
                  contentStyle={{
                    background: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid hsl(5, 30%, 90%)",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                  formatter={(value: number) => [
                    `${value}h`,
                    "Avg Response",
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="hours"
                  stroke="#ed4c4c"
                  strokeWidth={2}
                  fill="url(#responseGrad)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  )
}
